import * as THREE from 'three';
import { TICK_HZ } from '@somnhold/content';
import type { BattleState } from '@somnhold/sim';
import { simToWorld } from '@somnhold/render';
import { pinMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { h, setStyle } from '../ui/dom.ts';

/** Ceda: a woman's head and her staff with a leaf. */
const CEDA = '<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="7" cy="5" r="2.4" fill="currentColor"/><path d="M3 14.5c0-3.4 1.8-5.4 4-5.4s4 2 4 5.4z" fill="currentColor"/><path d="M13 2v12.5" stroke="currentColor" stroke-width="1.3"/><path d="M13 3c1.4-.2 2 .6 2 1.6-1.2.2-2-.4-2-1.6z" fill="currentColor"/></svg>';
/** A furrow: leaves heaped over something moving. */
const FURROW = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1.5 12.5c2-3.6 4.2-5.4 6.5-5.4s4.5 1.8 6.5 5.4z" fill="currentColor"/><path d="M2.5 14.5h3M7 14.5h2M11 14.5h2.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';
/** A spider's silk: a strand and its knot. */
const SILK = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 2.5l12 11M5 2.5c0 2 1 3 3 3M8 9.5c2 0 3 1 3 3" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><circle cx="8" cy="8" r="1.6" fill="currentColor"/></svg>';
/** A root, branching. */
const ROOT = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5V8M8 8c-1.6 1.6-3.8 2.2-5.5 2M8 8c1.6 1.6 3.8 2.2 5.5 2M8 8v6.5M8 11.5c-1 .9-2.2 1.3-3.4 1.3" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
/** The houses: two roofs. */
const HOUSE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1.5 8 5 4.5 8.5 8v6h-7zM8.5 9.5 11.5 6.5l3 3V14h-6z" fill="currentColor"/></svg>';
/** Paz: a head with its hair tied up, an axe. */
const PAZ = '<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="6.5" cy="6" r="2.4" fill="currentColor"/><path d="M4.4 4.4c.6-1.8 3.6-2.2 4.4-.2" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M2.5 14.5c0-3.2 1.8-5 4-5s4 1.8 4 5z" fill="currentColor"/><path d="M11.5 3.5v9M10 4.5l3-1.2v3z" stroke="currentColor" stroke-width="1.2" fill="currentColor"/></svg>';
/** A mushroom. */
const MUSH = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 8.5c0-3.4 2.7-5.5 6-5.5s6 2.1 6 5.5z" fill="currentColor"/><path d="M6.5 8.5h3l.5 5.5h-4z" fill="currentColor"/></svg>';
/** The maple's leaf. */
const LEAF = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5l1.4 3 2.6-1-.6 3 3-.2-2.2 2.4 1.6 1.6-3.4.4.4 3L8 11.6l-2.8 2.1.4-3-3.4-.4L3.8 8.7 1.6 6.3l3 .2-.6-3 2.6 1z" fill="currentColor"/><path d="M8 11.6v3" stroke="currentColor" stroke-width="1.2"/></svg>';
/** The broodmother: a fat spider. */
const SPIDER = '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="8" cy="9.5" rx="3.4" ry="3.8" fill="currentColor"/><circle cx="8" cy="4.8" r="2" fill="currentColor"/><path d="M4.6 8 1.5 5.5M4.6 10 1.2 10.5M4.8 12 2 14.5M11.4 8l3.1-2.5M11.4 10l3.4.5M11.2 12l2.8 2.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>';
/** A cut: a blade across a root. */
const CUT = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1.5 11.5h13" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/><path d="M9.5 1.5 6.5 14.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';

/**
 * The autumn wood on screen (docs/sendas/druida.md, chapter 3), inside a path's own plate (game/senda-hud.ts) and over
 * the board; a phone's plate kept short:
 *
 * - under the leaves (`leaves`): Ceda (walking, waiting for the hero, held: something at her; cocooned: cut it), the
 *   furrows (how many under the leaves; red while one heaves by her, its seconds; one held by the roots: strike it),
 *   the silk (red while a weaver spins: strike it).
 * - the memory (`blight`): the rot coming down (its heads, the nearest's cells to the houses), the next to come (which
 *   root, its seconds), Olmo's cut (still on the root: its share), Paz.
 * - what lies below (`under`, the test): the rot (asleep: look; awake: a stretch every 8 s), the rotten roots stopped
 *   in pips, the maple's branches in pips, the broodmother (her leap: get away; a web on the maple: its seconds), and
 *   the mushrooms' key (black: rotten under; white: sound).
 *
 * Over the board, one «Cortar» button: a tap by a root (the memory) or on a cell with roots (the test) walks the hero
 * there and offers its cut; the button sends it (the hero stands still there, its Raíz, its share filling the ring).
 * Plain DOM, written only when it changes (docs/RENDIMIENTO.md); the board's points from the sim's own rules.
 */
export class OtonoHud {
  readonly panel: HTMLElement;
  private kind: 'leaves' | 'blight' | 'under';
  private v = new THREE.Vector3();
  private rows: Record<string, { row: HTMLElement; text: HTMLElement }> = {};
  private pips: HTMLElement[] = [];
  private bpips: HTMLElement[] = [];
  private offerEl: HTMLButtonElement | null = null;
  private offerLabel: HTMLElement | null = null;
  private offerAt = -1;
  private offerPoint = new THREE.Vector3();

  constructor(
    s: BattleState,
    private board: HTMLElement,
    private mark: (at: number) => void,
  ) {
    const r = s.rules?.senda?.autumn;
    this.kind = r?.kind ?? 'leaves';
    const rows: HTMLElement[] = [];
    const row = (id: string, ico: string, label = ''): HTMLElement => {
      const text = h('span.al-wave');
      const el = h(`div.bh-row.ot-${id}`, {}, h('span.bh-ico', { html: ico }), label ? h('span.bh-label', {}, label) : '', text);
      this.rows[id] = { row: el, text };
      rows.push(el);
      return el;
    };
    if (this.kind === 'leaves') {
      row('ceda', CEDA, t('senda.otono.ceda'));
      row('furrow', FURROW);
      row('silk', SILK);
    } else if (this.kind === 'blight') {
      row('rot', ROOT);
      row('next', HOUSE);
      row('paz', PAZ);
    } else {
      row('rot', ROOT);
      const k = row('roots', CUT, t('senda.otono.roots'));
      const n = r?.under?.strands.filter((x) => x.rot).length ?? 4;
      this.pips = Array.from({ length: n }, () => h('i'));
      k.append(h('span.al-pips.ot-pips', {}, ...this.pips));
      // (Inside the Grandsire, the druid's chapter 5: the chamber where the maple stood, the old weaver for the broodmother.)
      const deep = s.rules?.place === 'deepNet';
      const m = row('maple', LEAF, t(deep ? 'senda.otono.chamber' : 'senda.otono.maple'));
      this.bpips = Array.from({ length: r?.under?.branches ?? 3 }, () => h('i'));
      m.append(h('span.al-pips.ot-branches', {}, ...this.bpips));
      row('boss', SPIDER, t(deep ? 'senda.otono.weaverOld' : 'senda.otono.boss'));
      // The mushrooms' key: what a black one and a white one say.
      rows.push(
        h(
          'div.bh-row.ot-key',
          {},
          h('span.bh-ico', { html: MUSH }),
          h('span.ot-mush.black', { html: MUSH }),
          h('span.al-wave', {}, t('senda.otono.keyBlack')),
          h('span.ot-mush.white', { html: MUSH }),
          h('span.al-wave', {}, t('senda.otono.keyWhite')),
        ),
      );
    }
    this.panel = h(`div.bh.ot-panel.ot-${this.kind}`, {}, ...rows);
    if (this.kind !== 'leaves') this.buildOffer();
  }

  /** The one «Cortar» button over the board. */
  private buildOffer(): void {
    const label = h('small.ot-offer-label', {}, t('senda.otono.cut'));
    const hint = t('senda.otono.cutHint');
    const el = h(
      'button.al-stone.ot-offer',
      { type: 'button', 'data-test': 'autumn-cut', 'aria-label': hint, title: hint, onpointerup: (e: Event) => (e.stopPropagation(), this.offerAt >= 0 && this.mark(this.offerAt)) },
      h('span.al-ring'),
      h('span.ot-glyph', { html: CUT }),
      label,
    ) as HTMLButtonElement;
    setStyle(el, 'display', 'none');
    this.board.append(el);
    this.offerEl = el;
    this.offerLabel = label;
  }

  /** A tap on the board at a sim point: by a root (or on a cell with roots), its cut offered; true when it was. */
  offer(s: BattleState, x: number, y: number): boolean {
    const r = s.rules?.senda?.autumn;
    const w = s.senda?.autumn;
    if (!r || !w || this.kind === 'leaves' || w.done >= 0) return false;
    const pts = r.blight ? r.blight.nodes : (r.under?.cells ?? []);
    let best = -1;
    let bd = (r.blight ? 650 : 620) ** 2;
    pts.forEach((p, i) => {
      if (r.blight && w.rot[i] === 2) return;
      if (r.under && w.cut[i]) return;
      const d = (p.x - x) ** 2 + (p.y - y) ** 2;
      if (d < bd) {
        bd = d;
        best = i;
      }
    });
    this.offerAt = best;
    if (best >= 0) {
      const p = pts[best] as { x: number; y: number };
      simToWorld(p.x, p.y, this.offerPoint, 0.9);
    }
    return best >= 0;
  }

  private set(id: string, text: string, cls?: 'warn' | 'good' | ''): void {
    const r = this.rows[id];
    if (!r) return;
    if (r.text.textContent !== text) r.text.textContent = text;
    r.row.classList.toggle('warn', cls === 'warn');
    r.row.classList.toggle('good', cls === 'good');
  }

  private hide(id: string, off: boolean): void {
    const r = this.rows[id];
    if (r && r.row.hidden !== off) r.row.hidden = off;
  }

  private updateLeaves(s: BattleState): void {
    const r = s.rules?.senda?.autumn?.leaves;
    const w = s.senda?.autumn;
    if (!r || !w) return;
    const ceda = s.senda?.npcs.find((n) => n.id === 'ceda');
    const hero = s.heroes.find((x) => x.downTicks === 0) ?? s.heroes[0];
    const far = !!ceda && !!hero && (hero.x - ceda.x) ** 2 + (hero.y - ceda.y) ** 2 > r.lead * r.lead;
    this.set(
      'ceda',
      w.cocoon ? t('senda.otono.cedaCocoon') : w.walk ? t('senda.otono.cedaWalk') : far ? t('senda.otono.cedaWait') : t('senda.otono.cedaHeld'),
      w.cocoon ? 'warn' : w.done >= 0 ? 'good' : '',
    );
    // The furrows: one heaving by her first; one held by the roots; else how many.
    let heave = -1;
    let held = 0;
    let under = 0;
    for (const id of Object.keys(w.sk)) {
      const m = w.sk[Number(id)] as number[];
      const e = s.enemies.find((x) => x.id === Number(id));
      if (!e || e.hp <= 0) continue;
      if (m[0] === 1) heave = heave < 0 ? (m[1] as number) : Math.min(heave, m[1] as number);
      else if (m[0] === 4) held++;
      else if (m[0] === 0 || m[0] === 3) under++;
    }
    if (heave >= 0) this.set('furrow', t('senda.otono.heave', { s: Math.max(1, Math.ceil((heave - s.tick) / TICK_HZ)) }), 'warn');
    else if (held) this.set('furrow', t('senda.otono.held'), 'good');
    else this.set('furrow', under ? t('senda.otono.under', { n: under }) : t('senda.otono.still'), '');
    let spin = -1;
    for (const id of Object.keys(w.wv)) {
      const m = w.wv[Number(id)] as number[];
      if ((m[1] as number) >= 0) spin = spin < 0 ? (m[1] as number) : Math.min(spin, m[1] as number);
    }
    this.hide('silk', spin < 0);
    if (spin >= 0) this.set('silk', t('senda.otono.spin', { s: Math.max(1, Math.ceil((spin - s.tick) / TICK_HZ)) }), 'warn');
  }

  private updateBlight(s: BattleState): void {
    const r = s.rules?.senda?.autumn?.blight;
    const w = s.senda?.autumn;
    if (!r || !w) return;
    // The nearest head to the houses: its cells down to an end.
    let near = 99;
    for (const hd of w.heads) {
      const depth = (i: number): number => {
        const n = r.nodes[i];
        if (!n || r.ends.includes(i)) return 0;
        let best = 99;
        for (const c of n.down) if (w.rot[c] !== 2) best = Math.min(best, 1 + depth(c));
        return best;
      };
      near = Math.min(near, depth(hd.i));
    }
    if (w.heads.length) this.set('rot', near < 99 ? t('senda.otono.rotNear', { n: w.heads.length, d: near }) : t('senda.otono.rotStopped', { n: w.heads.length }), near <= 3 ? 'warn' : '');
    else this.set('rot', w.done >= 0 ? t('senda.otono.rotDone') : t('senda.otono.rotNone'), w.done >= 0 ? 'good' : '');
    const nx = r.waves[w.wave];
    this.hide('next', !nx);
    if (nx) {
      const left = Math.max(0, Math.ceil((nx.t - s.waveT) / TICK_HZ));
      this.set('next', t('senda.otono.next', { root: t(`senda.otono.root${nx.src}`), s: left }), left <= 3 ? 'warn' : '');
    }
    const paz = s.senda?.npcs.find((n) => n.id === 'paz');
    this.set('paz', paz && paz.hp > 0 ? t(paz.hit && paz.hp < paz.maxHp ? 'senda.otono.pazHit' : 'senda.otono.paz', { n: paz.hp }) : '', paz && paz.hp <= 5 ? 'warn' : '');
  }

  private updateUnder(s: BattleState): void {
    const r = s.rules?.senda?.autumn?.under;
    const w = s.senda?.autumn;
    if (!r || !w || !w.front.length) return;
    const step = Math.max(1, Math.ceil((w.next - s.tick) / TICK_HZ));
    this.set('rot', w.done >= 0 ? t('senda.otono.out') : w.woke < 0 ? t('senda.otono.asleep', { s: Math.max(1, Math.ceil((r.wake - s.waveT) / TICK_HZ)) }) : t('senda.otono.awake', { s: step }), w.done >= 0 ? 'good' : w.woke >= 0 ? 'warn' : '');
    let k = 0;
    let stopped = 0;
    r.strands.forEach((st, i) => {
      if (!st.rot) return;
      const ok = (w.block[i] as number) >= 0 && (w.inMaple[i] as number) < 0;
      if (ok) stopped++;
      const p = this.pips[k++];
      if (p) p.className = ok ? 'covered' : (w.inMaple[i] as number) >= 0 ? 'burst' : '';
    });
    this.set('roots', t('senda.otono.rootsState', { n: stopped, of: this.pips.length }), stopped >= this.pips.length ? 'good' : '');
    this.bpips.forEach((p, i) => (p.className = i < w.lostB ? 'burst' : 'covered'));
    const deep = s.rules?.place === 'deepNet';
    this.set('maple', t(deep ? 'senda.otono.holds' : 'senda.otono.branches', { n: Math.max(0, r.branches - w.lostB), of: r.branches }), w.lostB >= r.branches - 1 && w.lostB > 0 ? 'warn' : '');
    let web = -1;
    for (const id of Object.keys(w.webs)) web = web < 0 ? (w.webs[Number(id)] as number) : Math.min(web, w.webs[Number(id)] as number);
    const bossText = w.bt >= 0 ? t('senda.otono.leap') : web >= 0 ? t(deep ? 'senda.otono.webDeep' : 'senda.otono.web', { s: Math.max(1, Math.ceil((web - s.tick) / TICK_HZ)) }) : t('senda.otono.about');
    this.set('boss', bossText, w.bt >= 0 || web >= 0 ? 'warn' : '');
  }

  update(s: BattleState): void {
    if (this.kind === 'leaves') this.updateLeaves(s);
    else if (this.kind === 'blight') this.updateBlight(s);
    else this.updateUnder(s);
    // The cut on offer: gone once done; while the hero stands on it, its share.
    const w = s.senda?.autumn;
    const r = s.rules?.senda?.autumn;
    const el = this.offerEl;
    if (!el || !w || !r) return;
    const job = w.job;
    if (job >= 0 && job !== this.offerAt) {
      this.offerAt = job;
      const p = r.blight ? r.blight.nodes[job] : r.under?.cells[job];
      if (p) simToWorld(p.x, p.y, this.offerPoint, 0.9);
    }
    const spent = this.offerAt >= 0 && ((r.blight && w.rot[this.offerAt] === 2) || (r.under && w.cut[this.offerAt] === 1));
    if (spent || w.done >= 0 || s.result !== 'running') this.offerAt = -1;
    const sent = job >= 0 && job === this.offerAt;
    el.classList.toggle('sent', sent);
    const need = r.blight?.cut ?? r.under?.cut ?? 24;
    el.style.setProperty('--k', sent ? Math.min(1, w.held / Math.max(1, need)).toFixed(2) : '0');
    const label = sent ? (w.held > 0 ? t('senda.otono.cutting') : t('senda.otono.going')) : t('senda.otono.cut');
    if (this.offerLabel && this.offerLabel.textContent !== label) this.offerLabel.textContent = label;
    el.disabled = s.result !== 'running';
  }

  /** Every frame: the offer over its place. */
  place(camera: THREE.Camera, w: number, ht: number): void {
    const el = this.offerEl;
    if (!el) return;
    if (this.offerAt < 0) {
      setStyle(el, 'display', 'none');
      return;
    }
    this.v.copy(this.offerPoint).project(camera);
    if (this.v.z > 1 || this.v.x < -1.1 || this.v.x > 1.1 || this.v.y < -1.1 || this.v.y > 1.1) {
      setStyle(el, 'display', 'none');
      return;
    }
    setStyle(el, 'display', '');
    pinMark(el, Math.min(w - 40, Math.max(40, (this.v.x * 0.5 + 0.5) * w)), (-this.v.y * 0.5 + 0.5) * ht);
  }
}
